import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { InPostLogo } from '@/components/inpost-logo';
import { ShopifyMark } from '@/components/shopify-logo';
import { useAuth } from '@/auth/useAuth';
import { api, ApiError } from '@/lib/api';

type Phase = 'pending' | 'error';

type InstallResponse = { authorize_url: string };

function LogoBox({ children }: { children: ReactNode }) {
  return (
    <div className="flex h-28 w-28 items-center justify-center rounded-xl bg-[#f5f5f5] p-5">
      {children}
    </div>
  );
}

function Connector({ animated }: { animated: boolean }) {
  return (
    <div className="flex items-center gap-2 px-4" aria-hidden>
      {[0, 1, 2].map((index) => (
        <span
          key={index}
          className={
            animated
              ? 'h-2.5 w-2.5 rounded-full bg-muted-foreground animate-connect-pulse'
              : 'h-2.5 w-2.5 rounded-full bg-muted-foreground/30'
          }
          style={animated ? { animationDelay: `${index * 200}ms` } : undefined}
        />
      ))}
    </div>
  );
}

function paramsToObject(params: URLSearchParams): Record<string, string> {
  const entries: Record<string, string> = {};
  params.forEach((value, key) => {
    entries[key] = value;
  });
  return entries;
}

export function ShopifyCallbackPage() {
  const auth = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [phase, setPhase] = useState<Phase>('pending');
  const [message, setMessage] = useState<string>('Connecting to Shopify…');
  const attempted = useRef(false);

  useEffect(() => {
    if (attempted.current) {
      return;
    }
    attempted.current = true;

    // Dev-only: visit `/auth/shopify/callback?preview=pending` (or `?preview=error`)
    // to inspect this screen without a real Shopify handshake. Stripped from prod builds.
    if (import.meta.env.DEV) {
      const preview = params.get('preview');
      if (preview === 'pending' || preview === 'error') {
        setPhase(preview);
        if (preview === 'error') {
          setMessage('Preview error - this is how a failed connection looks.');
        }
        return;
      }
    }

    const payload = paramsToObject(params);
    const hasHmac = typeof payload.hmac === 'string' && payload.hmac.length > 0;
    const hasCode = typeof payload.code === 'string' && payload.code.length > 0;
    const hasIdToken = typeof payload.id_token === 'string' && payload.id_token.length > 0;

    if (hasIdToken) {
      navigate('/shipments', { replace: true });
      return;
    }

    if (!hasHmac) {
      setPhase('error');
      setMessage('This page is only reachable through Shopify. Missing signed parameters.');
      return;
    }

    (async () => {
      try {
        if (hasCode) {
          await api.post<unknown>('/shopify/callback', payload, 'standalone');
          await auth.refresh();
          navigate('/shipments', { replace: true });
          return;
        }

        const { authorize_url } = await api.post<InstallResponse>(
          '/shopify/install',
          payload,
          'standalone',
        );
        window.location.href = authorize_url;
      } catch (error) {
        setPhase('error');
        setMessage(
          error instanceof ApiError
            ? `Shopify sign-in failed (${error.status}). Reopen the app from Shopify to try again.`
            : 'Shopify sign-in failed. Reopen the app from Shopify to try again.',
        );
      }
    })();
  }, [params, navigate, auth]);

  return (
    <div className="flex min-h-full w-full flex-col items-center justify-center gap-10 bg-background p-6">
      <div className="flex items-center justify-center">
        <LogoBox>
          <ShopifyMark className="h-14 w-14" />
        </LogoBox>
        <Connector animated={phase === 'pending'} />
        <LogoBox>
          <InPostLogo variant="white-bg" className="w-20" />
        </LogoBox>
      </div>

      {phase === 'pending' ? (
        <div className="max-w-md space-y-2 text-center">
          <h1 className="text-xl font-semibold text-foreground">Connecting to Shopify</h1>
          <p className="text-sm text-muted-foreground">
            Please keep this window open while we securely link your Shopify store with InPost. This
            only takes a moment.
          </p>
        </div>
      ) : (
        <div className="max-w-md space-y-3 text-center">
          <h1 className="text-xl font-semibold text-foreground">We couldn’t connect</h1>
          <p className="text-sm text-destructive">{message}</p>
          <a
            href="/login"
            className="inline-block text-sm font-medium text-primary underline-offset-4 hover:underline"
          >
            Back to sign-in
          </a>
        </div>
      )}
    </div>
  );
}
